import type { QueryTarget } from "@/shared/domain/query";

/**
 * Query key factory for View queries, shared by the read and write sides so they
 * cannot drift.
 *
 * Every level takes `orgId` first, so each cache slot is tenant-scoped. Switching
 * orgs is an in-SPA state change that leaves the query cache intact, so a key
 * without the org would serve the previous org's Views to the new one.
 *
 * Ids are org-scoped too, even though a `view_id` is globally unique: the same id
 * reads as a record in its own org and as absent in any other, so the answer
 * depends on who is asking.
 *
 * Lists are keyed by `target` below that, so Files and Datasets do not share an
 * entry. Keys are prefix-matched, so `lists(orgId)` still invalidates every
 * target at once.
 *
 * @see https://tkdodo.eu/blog/effective-react-query-keys#use-query-key-factories
 */
export const viewQueryKeys = {
  all: (orgId: string | undefined) => ["views", orgId] as const,
  lists: (orgId: string | undefined) =>
    [...viewQueryKeys.all(orgId), "list"] as const,
  list: (orgId: string | undefined, target: QueryTarget) =>
    [...viewQueryKeys.lists(orgId), target] as const,
  ids: (orgId: string | undefined) =>
    [...viewQueryKeys.all(orgId), "id"] as const,
  id: (orgId: string | undefined, viewId: string) =>
    [...viewQueryKeys.ids(orgId), viewId] as const,
};
